import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ExternalLink, Bookmark } from "lucide-react";
import api from "@/lib/api";
import ScoreBadge from "@/components/ScoreBadge";
import ScoreExplanation from "@/components/ScoreExplanation";
import DataQualityBadge from "@/components/DataQualityBadge";
import AIReport, { useAIReport } from "@/components/AIReport";
import SelectMenu from "@/components/SelectMenu";
import { fmtUSD, fmtPct, fmtNum, truncWallet, timeAgo } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function TraderDetail() {
  const { id } = useParams();
  const qc = useQueryClient();
  const [activityFilter, setActivityFilter] = useState("");
  const [summary, setSummary] = useState(null);
  const summaryMut = useAIReport(`/intel/traders/${id}/summary`, "trader");
  const { data, isLoading, isError } = useQuery({
    queryKey: ["trader", id],
    queryFn: async () => (await api.get(`/traders/${id}`)).data,
  });

  const { data: wls } = useQuery({
    queryKey: ["watchlists-trader"],
    queryFn: async () => (await api.get("/watchlists")).data,
  });

  const backfillMut = useMutation({
    mutationFn: async () => (await api.post(`/traders/${id}/backfill`)).data,
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["trader", id] });
      toast.success("Backfill complete", {
        description: `${r.positions_refreshed || 0} positions refreshed · ${r.positions_recorded || 0} historical positions closed.`,
      });
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Backfill failed"),
  });

  const watchMut = useMutation({
    mutationFn: async () => {
      const wl = (wls || []).find((w) => w.type === "trader") || (wls || [])[1] || (wls || [])[0];
      if (!wl) {
        throw new Error("No watchlist found. Create a trader watchlist first.");
      }
      await api.post(`/watchlists/${wl.id}/items/${id}`);
      return wl;
    },
    onSuccess: (wl) => {
      qc.invalidateQueries({ queryKey: ["watchlists-trader"] });
      qc.invalidateQueries({ queryKey: ["watchlists"] });
      toast.success(`Added to "${wl.name}"`, {
        description: t.public_username || truncWallet(t.wallet_address),
      });
    },
    onError: (err) => {
      toast.error(err?.message || "Failed to add to watchlist");
    },
  });

  // Whether this trader is already in ANY trader watchlist
  const isWatched = React.useMemo(() => {
    const traderLists = (wls || []).filter((w) => w.type === "trader" || w.type === undefined);
    return traderLists.some((w) => (w.item_ids || []).includes(id));
  }, [wls, id]);

  if (isLoading) return <div className="text-sm text-zinc-500">Loading trader…</div>;
  if (isError || !data) return <div className="text-sm text-rose-400">Trader not found.</div>;

  const t = data.trader;
  const s = data.score;
  const perf = data.performance;

  return (
    <div className="space-y-6" data-testid="trader-detail">
      <div>
        <Link to="/app/traders" className="text-xs text-zinc-500 hover:text-zinc-200 inline-flex items-center gap-1">
          <ArrowLeft size={12} /> Leaderboard
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-6">
        {/* Identity */}
        <div className="lg:col-span-1 border border-zinc-800 rounded-md p-4 space-y-3">
          <div className="w-14 h-14 rounded-full bg-zinc-800 overflow-hidden grid place-items-center text-zinc-400 text-sm font-mono-num">
            {t.image_url ? (
              <img src={t.image_url} alt="" className="w-full h-full object-cover" />
            ) : (
              (t.public_username || "?").slice(0, 2).toUpperCase()
            )}
          </div>
          <div>
            <div className="font-heading text-lg font-semibold text-zinc-100">
              {t.public_username || t.display_name || "Anonymous trader"}
            </div>
            <div className="text-[11px] font-mono-num text-zinc-500 mt-0.5">
              {truncWallet(t.wallet_address)}
            </div>
          </div>
          {t.bio && <div className="text-xs text-zinc-400">{t.bio}</div>}
          <div className="flex items-center gap-2">
            <ScoreBadge score={t.latest_score || s?.total_score} size="lg" testid="trader-score" />
            {s && (
              <ScoreExplanation
                score={s.total_score}
                components={s.components}
                weights={s.weights}
                missing={s.missing_components}
                confidence={s.confidence}
                formulaVersion={s.formula_version}
              />
            )}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {(data.archetype?.labels || []).map((l) => (
              <span key={l} className="text-[10px] uppercase tracking-wider bg-zinc-800/70 text-zinc-300 px-1.5 py-0.5 rounded-sm">
                {l}
              </span>
            ))}
          </div>
          {t.public_profile_url && (
            <a
              href={t.public_profile_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-zinc-100"
              data-testid="link-polymarket-profile"
            >
              Polymarket profile <ExternalLink size={11} />
            </a>
          )}
          <Button
            size="sm"
            onClick={() => watchMut.mutate()}
            disabled={watchMut.isPending || isWatched}
            data-testid="btn-follow-trader"
            className={`w-full h-8 text-xs ${
              isWatched
                ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 cursor-default"
                : "bg-zinc-800 hover:bg-zinc-700 text-zinc-100"
            }`}
          >
            <Bookmark size={11} className="mr-1" />
            {isWatched ? "In your watchlist" : (watchMut.isPending ? "Adding…" : "Add to watchlist")}
          </Button>
          <DataQualityBadge status={t.data_quality || "partial"} />
        </div>

        {/* Right column: performance + positions */}
        <div className="lg:col-span-3 space-y-5">
          {/* AI Trader Intelligence Executive Summary (M5) */}
          <AIReport
            title="Trader intelligence · Executive summary"
            report={summary}
            isGenerating={summaryMut.isPending}
            onGenerate={() => summaryMut.mutateAsync().then(setSummary).catch(() => {})}
            ctaLabel="Generate summary"
            testid="trader-summary"
            quotaEndpoint="trader_summary"
          />

          <section>
            {(perf.resolved_markets === 0 || perf.win_rate == null) && (
              <div className="mb-3 border border-amber-500/30 bg-amber-500/5 rounded-md px-3 py-2 text-xs text-amber-200 flex items-start gap-2"
                   data-testid="perf-insufficient-notice">
                <span className="mt-0.5">ⓘ</span>
                <div className="flex-1">
                  <div>
                    <span className="font-heading">Historical resolution data limited.</span>{" "}
                    Realized P&amp;L, resolved markets, and win rate rely on markets we&apos;ve
                    watched through to resolution. Ingestion improves this over time.
                  </div>
                  <Button
                    size="sm"
                    onClick={() => backfillMut.mutate()}
                    disabled={backfillMut.isPending}
                    data-testid="btn-backfill-trader"
                    className="mt-2 h-7 text-[11px] bg-amber-500/20 hover:bg-amber-500/30 text-amber-100 border border-amber-500/40"
                  >
                    {backfillMut.isPending ? "Backfilling…" : "Backfill this trader now"}
                  </Button>
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: "Total P&L (est.)", value: fmtUSD(perf.total_estimated_pnl), cls: (perf.total_estimated_pnl || 0) >= 0 ? "text-emerald-400" : "text-rose-400" },
                { label: "Realized (est.)", value: perf.estimated_realized_pnl ? fmtUSD(perf.estimated_realized_pnl) : "Insufficient data", cls: "" },
                { label: "Unrealized (est.)", value: fmtUSD(perf.estimated_unrealized_pnl), cls: "" },
                { label: "Active positions", value: fmtNum(perf.active_positions) },
                { label: "Resolved markets", value: perf.resolved_markets ? fmtNum(perf.resolved_markets) : "Insufficient data" },
                { label: "Win rate", value: perf.win_rate != null ? fmtPct(perf.win_rate) : "Insufficient data" },
              ].map((k) => (
                <div key={k.label} className="border border-zinc-800 rounded-md p-3">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">{k.label}</div>
                  <div className={`font-mono-num text-lg mt-1 text-zinc-100 ${k.cls || ""}`}>
                    {k.value}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Category performance */}
          {Object.keys(data.category_scores || {}).length > 0 && (
            <section>
              <h2 className="font-heading text-lg font-semibold text-zinc-100 mb-2">Category performance</h2>
              <div className="border border-zinc-800 rounded-sm overflow-x-auto">
                <table className="w-full min-w-[640px] text-sm">
                  <thead className="text-[10px] uppercase tracking-widest text-zinc-500 bg-zinc-900/40">
                    <tr>
                      <th className="text-left px-3 py-2">Category</th>
                      <th className="text-right px-3 py-2">Sample</th>
                      <th className="text-right px-3 py-2">Win rate</th>
                      <th className="text-right px-3 py-2">Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.values(data.category_scores).map((c) => (
                      <tr key={c.category} className="border-t border-zinc-800/70">
                        <td className="px-3 py-2">{c.category}</td>
                        <td className="px-3 py-2 text-right font-mono-num">{fmtNum(c.sample_size)}</td>
                        <td className="px-3 py-2 text-right font-mono-num">
                          {c.components?.win_rate != null ? fmtPct(c.components.win_rate) : "—"}
                        </td>
                        <td className="px-3 py-2 text-right"><ScoreBadge score={c.score} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* Current positions */}
          <section>
            <h2 className="font-heading text-lg font-semibold text-zinc-100 mb-2">Current positions</h2>
            <div className="border border-zinc-800 rounded-sm overflow-x-auto">
              <table className="w-full text-sm min-w-[800px]">
                <thead className="text-[10px] uppercase tracking-widest text-zinc-500 bg-zinc-900/40">
                  <tr>
                    <th className="text-left px-3 py-2">Market</th>
                    <th className="text-left px-3 py-2">Outcome</th>
                    <th className="text-right px-3 py-2">Size</th>
                    <th className="text-right px-3 py-2">Avg</th>
                    <th className="text-right px-3 py-2">Current</th>
                    <th className="text-right px-3 py-2">Value</th>
                    <th className="text-right px-3 py-2">P&amp;L (est.)</th>
                  </tr>
                </thead>
                <tbody className="stagger-in">
                  {data.current_positions.length === 0 && (
                    <tr><td colSpan={7} className="text-xs text-zinc-500 text-center py-6">No open positions detected.</td></tr>
                  )}
                  {data.current_positions.map((p) => (
                    <tr key={p.id} className="border-t border-zinc-800/70 hover:bg-zinc-900/40 transition-colors"
                        data-testid={`position-${p.id}`}>
                      <td className="px-3 py-2">
                        <Link to={`/app/markets/${p.market_id}`} className="text-zinc-100 hover:underline line-clamp-1">
                          {p.market_title}
                        </Link>
                        <div className="text-[10px] text-zinc-500 mt-0.5">{p.market_category || "—"}</div>
                      </td>
                      <td className="px-3 py-2 text-zinc-300">{p.outcome}</td>
                      <td className="px-3 py-2 text-right font-mono-num">{fmtNum(p.size, 2)}</td>
                      <td className="px-3 py-2 text-right font-mono-num">{p.average_price != null ? fmtPct(p.average_price) : "—"}</td>
                      <td className="px-3 py-2 text-right font-mono-num">{p.current_price != null ? fmtPct(p.current_price) : "—"}</td>
                      <td className="px-3 py-2 text-right font-mono-num">{fmtUSD(p.current_value)}</td>
                      <td className={`px-3 py-2 text-right font-mono-num ${
                        (p.cash_pnl || 0) > 0 ? "text-emerald-400" : (p.cash_pnl || 0) < 0 ? "text-rose-400" : "text-zinc-300"
                      }`}>{fmtUSD(p.cash_pnl)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Activity */}
          <section>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-2">
              <h2 className="font-heading text-lg font-semibold text-zinc-100">Activity timeline · Conviction</h2>
              <SelectMenu
                testid="filter-activity-type"
                value={activityFilter}
                onChange={setActivityFilter}
                className="w-52"
                options={[
                  { value: "", label: "All actions" },
                  { value: "POSITION_OPENED", label: "Opened" },
                  { value: "POSITION_INCREASED", label: "Increased" },
                  { value: "POSITION_REDUCED", label: "Reduced" },
                  { value: "POSITION_CLOSED", label: "Closed" },
                  { value: "DIRECTION_FLIPPED", label: "Flipped direction" },
                  { value: "AVERAGED_UP", label: "Averaged up" },
                  { value: "AVERAGED_DOWN", label: "Averaged down" },
                  { value: "PROFIT_TAKING_POSSIBLE", label: "Profit taking (possible)" },
                ]}
              />
            </div>
            <div className="border border-zinc-800 rounded-sm divide-y divide-zinc-800/70">
              {(data.activities || []).filter(a => !activityFilter || a.activity_type === activityFilter).length === 0 && (
                <div className="p-4 text-xs text-zinc-500">No matching activity.</div>
              )}
              {(data.activities || []).filter(a => !activityFilter || a.activity_type === activityFilter).map((a) => (
                <div key={a.id} className="px-3 py-2 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between text-sm sm:gap-3" data-testid={`activity-${a.id}`}>
                  <div>
                    <div className="text-zinc-200"><span className="text-xs font-mono-num text-zinc-400">{(a.activity_type || "activity").replaceAll("_", " ")}</span> — {a.interpretation}</div>
                    <div className="text-[10px] text-zinc-500 mt-0.5">Market {a.market_id?.slice(0, 12)}… · confidence {Math.round((a.interpretation_confidence || 0) * 100)}%</div>
                  </div>
                  <div className="text-[11px] text-zinc-500 whitespace-nowrap">{timeAgo(a.occurred_at)}</div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
